import { Pagination } from '@/constants/pagination';
import { PaginationAPI } from '@/types/pagination';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type GroupCardFilterState = {
    search?: string;
};

export type CardState = {
    data: CardType[];
    cardDetail: CardType | null;
    pagination: PaginationAPI;
    totalElements?: number;
    loading: boolean;
    filters: CardFilterState;
    groupCardFilters: GroupCardFilterState;
    cardType: CardTypeType[];
    cardStatus: CardStatusType[];
    cardGroup: CardGroupType[];
    cardGroupDetail: CardGroupType | null;
    cardDetailAction: ResourcePermissionCheck | null;
    cardLimits: SpendingLimit | null;
    merchantCategories: MerchantCategory[];
    merchantCategoriesPagination: PaginationAPI;
    merchants: Merchant[];
    merchantsPagination: PaginationAPI;
    countries: any[];
};

export const CardInitialState: CardState = {
    data: [],
    cardDetail: null,
    pagination: {
        Page: Pagination.Page,
        totalPages: 0,
        totalElements: 0,
        Size: Pagination.Size,
    },
    filters: {},
    groupCardFilters: {},
    loading: false,
    cardType: [],
    cardStatus: [],
    cardGroup: [],
    cardGroupDetail: null,
    cardDetailAction: null,
    cardLimits: null,
    merchantCategories: [],
    merchantCategoriesPagination: {
        Page: Pagination.Page,
        totalPages: 0,
        totalElements: 0,
        Size: Pagination.Size,
    },
    merchants: [],
    merchantsPagination: {
        Page: Pagination.Page,
        totalPages: 0,
        totalElements: 0,
        Size: Pagination.Size,
    },
    countries: [],
};

const slice = createSlice({
    name: 'card',
    initialState: CardInitialState,
    reducers: {
        setListCardAction(
            state,
            action: PayloadAction<{
                data: CardType[];
                pagination?: PaginationAPI;
                append?: boolean;
            }>,
        ) {
            const { payload } = action;
            if (payload.append && payload.data) {
                // Append new data to existing data
                state.data = [...state.data, ...payload.data];
            } else {
                // Replace data
                state.data = payload.data;
            }
            state.pagination = payload.pagination || state.pagination;
        },
        setListCardGroupAction(
            state,
            action: PayloadAction<{
                cardGroup: CardGroupType[];
                pagination?: PaginationAPI;
            }>,
        ) {
            const { payload } = action;
            state.cardGroup = payload.cardGroup;
            state.pagination = payload.pagination || state.pagination;
        },
        setCardLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setCardTypeAction(
            state,
            action: PayloadAction<{
                cardType: CardTypeType[]
            }>
        ) {
            const { payload } = action;
            state.cardType = payload.cardType;
        },
        setCardStatusAction(
            state,
            action: PayloadAction<{
                cardStatus: CardStatusType[]
            }>
        ) {
            const { payload } = action;
            state.cardStatus = payload.cardStatus;
        },
        setCardFiltersAction(
            state,
            action: PayloadAction<CardFilterState>
        ) {
            state.filters = { ...state.filters, ...action.payload };
        },
        resetCardFiltersAction(state) {
            state.filters = {};
        },
        setGroupCardFiltersAction(
            state,
            action: PayloadAction<GroupCardFilterState>
        ) {
            state.groupCardFilters = { ...state.groupCardFilters, ...action.payload };
        },
        resetGroupCardFiltersAction(state) {
            state.groupCardFilters = {};
        },
        updateCardAction(
            state,
            {
                payload,
            }: PayloadAction<{
                id: string;
                changes: Partial<CardType>;
            }>,
        ) {
            state.data = state.data.map((item) => {
                if (item.id === payload.id) {
                    // merge partial changes with existing card
                    return {
                        ...item,
                        ...payload.changes,
                    };
                }
                return item;
            });
        },
        setGroupCardDetailAction(
            state,
            action: PayloadAction<{
                data: CardGroupType
            }>
        ) {
            const { payload } = action;
            state.cardGroupDetail = payload.data;
        },
        setCardAction(
            state,
            action: PayloadAction<{
                data: ResourcePermissionCheck
            }>
        ) {
            const { payload } = action;
            state.cardDetailAction = payload.data;
        },
        setCardLimitsAction(
            state,
            action: PayloadAction<{
                data: SpendingLimit
            }>
        ) {
            const { payload } = action;
            state.cardLimits = payload.data;
        },
        setMerchantCategoriesAction(
            state,
            action: PayloadAction<{
                data: MerchantCategory[];
                append?: boolean;
                pagination?: PaginationAPI;
            }>
        ) {
            const { payload } = action;
            if (payload.append && payload.data) {
                state.merchantCategories = [...state.merchantCategories, ...payload.data];
            } else {
                state.merchantCategories = payload.data;
            }
            if (payload.pagination) {
                state.merchantCategoriesPagination = payload.pagination;
            }
        },
        setMerchantsAction(
            state,
            action: PayloadAction<{
                data: Merchant[];
                append?: boolean;
                pagination?: PaginationAPI;
            }>
        ) {
            const { payload } = action;
            if (payload.append && payload.data) {
                state.merchants = [...state.merchants, ...payload.data];
            } else {
                state.merchants = payload.data;
            }
            if (payload.pagination) {
                state.merchantsPagination = payload.pagination;
            }
        },
        setCountriesAction(
            state,
            action: PayloadAction<{
                data: any[];
            }>
        ) {
            state.countries = action.payload.data;
        },
    },
});

export const {
    setListCardAction,
    setCardLoading,
    setCardFiltersAction,
    resetCardFiltersAction,
    setGroupCardFiltersAction,
    resetGroupCardFiltersAction,
    updateCardAction,
    setCardTypeAction,
    setCardStatusAction,
    setGroupCardDetailAction,
    setListCardGroupAction,
    setCardAction,
    setCardLimitsAction,
    setMerchantCategoriesAction,
    setMerchantsAction,
    setCountriesAction
} = slice.actions;
export default slice.reducer;
